import {
  blue,
  cyan,
  deepPurple,
  green,
  grey,
  lightBlue,
  lightGreen,
  lime,
  orange,
  pink,
  purple,
  red,
  yellow,
} from '@mui/material/colors';
import {
  type Color,
  type PaletteMode,
  type Theme,
  alpha,
  createTheme,
} from '@mui/material/styles';
import {
  type BrandColors,
  resolvePrimaryMain,
  resolveSecondaryMain,
} from './brandPalette';

declare module '@mui/material/styles' {
  interface Palette {
    yellow: Color;
    orange: Color;
    purple: Color;
    pink: Color;
    lightBlue: Color;
    cyan: Color;
    blue: Color;
    deepPurple: Color;
    lime: Color;
    lightGreen: Color;
    green: Color;
    red: Color;
  }
  interface PaletteOptions {
    yellow?: Partial<Color>;
    orange?: Partial<Color>;
    purple?: Partial<Color>;
    pink?: Partial<Color>;
    lightBlue?: Partial<Color>;
    cyan?: Partial<Color>;
    blue?: Partial<Color>;
    deepPurple?: Partial<Color>;
    lime?: Partial<Color>;
    lightGreen?: Partial<Color>;
    green?: Partial<Color>;
    red?: Partial<Color>;
  }
}

export const materialSwatchColors = {
  grey,
  yellow,
  orange,
  purple,
  pink,
  lightBlue,
  cyan,
  blue,
  deepPurple,
  lime,
  lightGreen,
  green,
  red,
} as const;

export type MaterialSwatchPaletteKey = keyof typeof materialSwatchColors;

export function getMaterialSwatchStageStyles(
  theme: Theme,
  paletteKey: MaterialSwatchPaletteKey,
  options?: { highlighted?: boolean }
) {
  const palette = theme.palette[paletteKey];
  const isDark = theme.palette.mode === 'dark';
  const tint = isDark ? palette[800] : palette[300];
  const borderTint = isDark ? palette[600] : palette[300];
  return {
    textColor: isDark ? palette[300] : palette[900],
    borderColor: alpha(borderTint, isDark ? 0.5 : 1),
    backgroundColor: alpha(
      tint,
      options?.highlighted ? 0.5 : isDark ? 0.35 : 0.25
    ),
  };
}

export const createBrandTheme = (
  brandColors: BrandColors,
  mode: PaletteMode
) => {
  const isDark = mode === 'dark';
  const baseTheme = createTheme({ palette: { mode } });

  return createTheme(baseTheme, {
    palette: {
      primary: baseTheme.palette.augmentColor({
        color: { main: resolvePrimaryMain(brandColors, mode) },
        name: 'primary',
      }),
      secondary: baseTheme.palette.augmentColor({
        color: { main: resolveSecondaryMain(brandColors, mode) },
        name: 'secondary',
      }),
      ...materialSwatchColors,
      ...(!isDark && {
        background: {
          default: '#f5f5f5',
        },
      }),
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.25rem',
      },
      h2: {
        fontWeight: 600,
        fontSize: '2rem',
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
      },
      h6: {
        fontWeight: 600,
        fontSize: '1.125rem',
      },
      subtitle1: {
        fontSize: '1.125rem',
        lineHeight: 1.5,
      },
      subtitle2: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '1rem',
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.4,
      },
      caption: {
        fontSize: '0.8125rem',
        lineHeight: 1.4,
      },
      button: {
        fontSize: '1rem',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: (theme: Theme) => {
          const thumb = isDark
            ? theme.palette.grey[600]
            : theme.palette.grey[400];
          const thumbHover = theme.palette.grey[500];

          return {
            '*': {
              scrollbarWidth: 'thin',
              scrollbarColor: `${thumb} transparent`,
            },
            '*::-webkit-scrollbar': {
              width: 8,
              height: 8,
            },
            '*::-webkit-scrollbar-thumb': {
              backgroundColor: thumb,
              borderRadius: 8,
            },
            '*::-webkit-scrollbar-thumb:hover': {
              backgroundColor: thumbHover,
            },
            '*::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
          };
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: ({ theme }: { theme: Theme }) => ({
            [theme.breakpoints.down('sm')]: {
              fontSize: '16px',
            },
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            border: 'none',
            backgroundColor: 'background.default',
            zIndex: 1100,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.16)',
            },
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            boxShadow: 'none',
            border: 'none',
            backgroundColor: 'background.default',
            zIndex: 1200,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '--Paper-shadow': 'none',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
          },
        },
        defaultProps: {
          maxWidth: 'md',
          fullWidth: true,
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '24px 24px 16px 24px',
          },
        },
        defaultProps: {
          component: 'h2',
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '24px',
            '&:first-of-type': {
              paddingTop: '16px',
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px 24px 24px',
            gap: 4,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: ({ theme }: { theme: Theme }) => ({
            width: 35,
            height: 35,
            fontSize: '1rem',
            fontWeight: 500,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }),
          img: {
            objectFit: 'cover',
          },
        },
        defaultProps: {
          variant: 'circular',
        },
      },
      MuiChip: {
        styleOverrides: {
          sizeSmall: {
            '& .MuiChip-label': {
              fontSize: '0.8125rem',
            },
          },
        },
      },
    },
  });
};
